import * as tf from "@tensorflow/tfjs-core";
import { setThreadsCount } from "@tensorflow/tfjs-backend-wasm";
import "@tensorflow/tfjs-backend-wasm";
import "@tensorflow/tfjs-backend-webgl";

/* ================== CONSTANTS ================== */

// NOTE: index 0 is intentionally empty because the dequantization step adds +1
// to the raw class id (see runCarPartDetection). So raw model class N maps to
// CLASS_LABELS[N + 1]. Raw model class order (0-indexed):
//   front_glass, hood, front_bumper, headlight, back_bumper, front_door,
//   back_door, fender, qtr_panel, window_glass, wheel, dicky, back_glass,
//   taillight, running_board, side_view_mirror, non_van, van
const CLASS_LABELS = [
  "",
  "front_glass",
  "hood",
  "front_bumper",
  "headlight",
  "back_bumper",
  "front_door",
  "back_door",
  "fender",
  "qtr_panel",
  "window_glass",
  "wheel",
  "dicky",
  "back_glass",
  "taillight",
  "running_board",
  "side_view_mirror",
  "non_van",
  "van",
];


export const CLASS_LABELS_OTHER = [
  "front_glass",
  "hood",
  "front_bumper",
  "headlight",
  "back_bumper",
  "front_door",
  "back_door",
  "fender",
  "qtr_panel",
  "window_glass",
  "wheel",
  "dicky",
  "back_glass",
  "taillight",
  "running_board",
  "side_view_mirror",
  "non_van",
  "van",
];

// Two threshold sets: non-van (default) and van. Which one is used per frame is
// decided at inference time from the model's own van / non_van prediction.
const CLASS_THRESHOLDS = new Float32Array(19).fill(0.7);
const CLASS_THRESHOLDS_VAN = new Float32Array(19).fill(0.7);

// Same threshold values as before, remapped to the new class indices:
//   front_door -> 0.6, fender -> 0.4, qtr_panel -> 0.4, window_glass -> 0.4,
//   wheel -> 0.45, back_glass -> 0.4, running_board -> 0.3
const customThresholds = {
  6: 0.6,   // front_door
  8: 0.4,   // fender
  9: 0.4,   // qtr_panel
  10: 0.4,  // window_glass
  11: 0.45, // wheel
  13: 0.6,  // back_glass
  15: 0.3,  // running_board
};
// Van-specific thresholds, mapped to CLASS_LABELS indices (note the +1 offset,
// so front_glass = 1, not 0). Parts not listed keep the default 0.7.
const customThresholdsVan = {
  1: 0.7,   // front_glass
  2: 0.6,   // hood
  3: 0.7,   // front_bumper
  4: 0.3,   // headlight
  5: 0.4,  // back_bumper
  6: 0.6,   // front_door
  7: 0.6,   // back_door
  8: 0.3,   // fender
  9: 0.3,   // qtr_panel
  10: 0.6,  // window_glass
  11: 0.3,  // wheel
  12: 0.6,  // dicky
  13: 0.6,  // back_glass
  14: 0.3,  // taillight
  15: 0.3,  // running_board
};



Object.entries(customThresholds).forEach(
  ([idx, val]) => (CLASS_THRESHOLDS[idx] = val)
);
Object.entries(customThresholdsVan).forEach(
  ([idx, val]) => (CLASS_THRESHOLDS_VAN[idx] = val)
);

/* ================== QUANTIZATION ================== */

// Dequant params — filled in at model-load time by loadDequantParamsFromModel,
// which reads the real scale + zero_point straight out of the .tflite file.
// No hardcoded values, so swapping in a new int8 model needs no code changes.
let S_SCORE, S_BOX, Z_BOX, S_CLASS, S_NUM;


/* ================== LOAD MODEL ================== */

// tfjs-tflite (window.tflite) is loaded from the CDN in public/index.html. By
// default it fetches its WASM runtime (tflite_web_api_client*.js / *.wasm)
// relative to the app's own origin, where those files do NOT exist — so in
// production the runtime 404s, the Emscripten Module never instantiates, and
// loadTFLiteModel throws "Cannot read properties of undefined (reading
// '_malloc')". Point it at the CDN folder that actually hosts the WASM runtime.
// NOTE: keep this version in sync with the tfjs-tflite <script> in index.html.
const TFLITE_WASM_PATH =
  "https://cdn.jsdelivr.net/npm/@tensorflow/tfjs-tflite@0.0.1-alpha.10/wasm/";

let tfliteWasmPathSet = false;

// Model file location (used for both reading quant params and tfjs-tflite load).
const MODEL_URL = "/model_int8.tflite";

// Read the 4 output tensors' quantization (scale + zero_point) directly from the
// .tflite flatbuffer, in the same order tfjs-tflite returns them
// ([scores, boxes, count, classes]). No TF / library needed — we walk the
// flatbuffer tables by hand. Throws on failure so model init fails loudly
// instead of running with missing/incorrect dequant params.
async function loadDequantParamsFromModel(url) {
  try {
    const resp = await fetch(url);
    const buf = await resp.arrayBuffer();
    const dv = new DataView(buf);
    const u16 = (o) => dv.getUint16(o, true);
    const u32 = (o) => dv.getUint32(o, true);
    const i32 = (o) => dv.getInt32(o, true);
    const f32 = (o) => dv.getFloat32(o, true);

    // Field offset within a flatbuffer table (0 => field absent).
    const foff = (table, fid) => {
      const vt = table - i32(table); // vtable is at table - soffset
      if (4 + fid * 2 >= u16(vt)) return 0;
      return u16(vt + 4 + fid * 2);
    };
    // Follow a relative uint32 offset (tables / vectors / strings).
    const deref = (pos) => pos + u32(pos);

    const root = u32(0); // Model table
    const sgVec = deref(root + foff(root, 2)); // Model.subgraphs (field 2)
    const subgraph = deref(sgVec + 4); // first subgraph
    const tensorsStart = deref(subgraph + foff(subgraph, 0)) + 4; // SubGraph.tensors (0)
    const outputsVec = deref(subgraph + foff(subgraph, 2)); // SubGraph.outputs (2)
    const nOut = u32(outputsVec);

    const quantOf = (tensorIdx) => {
      const t = deref(tensorsStart + 4 * tensorIdx);
      const qo = foff(t, 4); // Tensor.quantization (field 4)
      if (!qo) return null;
      const q = deref(t + qo);
      let scale = null;
      let zeroPoint = 0;
      const so = foff(q, 2); // scale: vector<float> (field 2)
      if (so) {
        const v = deref(q + so);
        if (u32(v) > 0) scale = f32(v + 4);
      }
      const zo = foff(q, 3); // zero_point: vector<long> (field 3)
      if (zo) {
        const v = deref(q + zo);
        if (u32(v) > 0) zeroPoint = i32(v + 4); // low 32 bits is enough for zp
      }
      return { scale, zeroPoint };
    };

    const out = [];
    for (let i = 0; i < nOut; i++) {
      out.push(quantOf(i32(outputsVec + 4 + 4 * i)));
    }
    if (out.length !== 4) throw new Error(`expected 4 outputs, got ${out.length}`);

    // Order matches `const [scoresT, boxesT, numT, classT] = tensors`.
    const [scores, boxes, count, classes] = out;
    if (scores && scores.scale != null) S_SCORE = scores.scale;
    if (boxes && boxes.scale != null) {
      S_BOX = boxes.scale;
      Z_BOX = boxes.zeroPoint;
    }
    if (count && count.scale != null) S_NUM = count.scale;
    if (classes && classes.scale != null) S_CLASS = classes.scale;

    if (![S_SCORE, S_BOX, Z_BOX, S_CLASS, S_NUM].every(Number.isFinite)) {
      throw new Error(
        "missing quant params on model output tensors (scores/boxes/count/classes)",
      );
    }

  } catch (e) {
    throw new Error(
      `[dequant] could not read quant params from model: ${e.message}`,
    );
  }
}

export async function loadCarPartModel() {
  const now = performance.now();
  try {
    // Tell WASM to use multiple CPU cores for maximum speed
    const cores = navigator.hardwareConcurrency || 4;
    const threadsToUse = Math.max(1, Math.floor(cores / 2));
    setThreadsCount(threadsToUse);

    await tf.setBackend("webgl");
    await tf.ready();
    // console.log(`Successfully initialized WebGL backend with ${threadsToUse} threads for int8 optimization`);
  } catch (error) {
    // console.warn("WebGL failed to initialize. Falling back to WASM.", error);
    await tf.setBackend("wasm");
    await tf.ready();
    // console.log("Successfully initialized WASM backend");
  }

  if (!window.tflite || typeof window.tflite.loadTFLiteModel !== "function") {
    throw new Error(
      "tfjs-tflite (window.tflite) is not available. Check that the CDN script in index.html loaded before model initialization."
    );
  }

  // Must be set before the first loadTFLiteModel call.
  if (!tfliteWasmPathSet) {
    window.tflite.setWasmPath(TFLITE_WASM_PATH);
    tfliteWasmPathSet = true;
  }

  // Pull the real dequant params out of the model file before first inference.
  await loadDequantParamsFromModel(MODEL_URL);

  const model = await window.tflite.loadTFLiteModel(MODEL_URL);
  const end = performance.now();
  return { model, time: end - now };
}

/* ================== PREPROCESS ================== */

/* ================== PREPROCESS ================== */

export const preprocessVideo = (canvas) => {
  return tf.tidy(() => {
    return tf.browser
      .fromPixels(canvas)
      .expandDims(0)
      .cast("int32"); // RESTORED INT32: The model needs this to map to 'uint8'
  });
};

/* ================== INFERENCE ================== */

// Last van / non_van classification (max scores + verdict) from the most recent
// runCarPartDetection call. van/non_van are used only to pick the threshold set
// and are then filtered out of the returned detections, so this getter is the
// only way for the UI to see what the model classified the vehicle as.
let lastVanInfo = { van: 0, nonVan: 0, result: "none" };
export const getLastVanInfo = () => lastVanInfo;

const removeVanDetections = (detections) => {
  if (!detections) return detections;
  return detections.filter(
    (item) => item.label !== "van" && item.label !== "non_van",
  );
};

export async function runCarPartDetection(model, inputTensor) {

  const result = model.predict(inputTensor);
  const outputs = Array.isArray(result) ? result : [result];
  const tensors = Object.values(outputs[0] || outputs);

  if (tensors.length !== 4) {
    cleanupTensors(outputs);
    // Note: inputTensor.dispose() is deliberately omitted here so StencilVideo.js doesn't crash
    return [];
  }

  const [scoresT, boxesT, numT, classT] = tensors;

  // =========================================================
  // OPTIMIZATION: Vectorize the math! 
  // This does all the heavy math on the hardware backend instantly
  // =========================================================
  const processedTensors = tf.tidy(() => {
    return [
      tf.mul(scoresT, S_SCORE),                                     // Dequantize Scores
      tf.mul(tf.sub(boxesT, Z_BOX), S_BOX),                         // Dequantize Boxes
      tf.round(tf.mul(numT, S_NUM)),                                // Dequantize Count
      tf.add(tf.round(tf.mul(classT, S_CLASS)), 1)                  // Dequantize Classes
    ];
  });

  const [dScoresT, dBoxesT, dNumT, dClassesT] = processedTensors;

  // ASYNC EXTRACTION: Download the pre-calculated data without freezing the UI
  const [scoresData, boxesData, numData, classData] = await Promise.all([
    dScoresT.data(),
    dBoxesT.data(),
    dNumT.data(),
    dClassesT.data()
  ]);

  const count = Math.min(numData[0], scoresData.length);

  // First pass: check what the model says the vehicle is. If it predicts "van"
  // use the van threshold set (customThresholdsVan); otherwise ("non_van" or
  // neither above the 0.05 noise floor) use the default set (customThresholds).
  // Highest-scoring of the two van/non_van predictions wins.
  let vanScore = 0;
  let nonVanScore = 0;
  for (let i = 0; i < count; i++) {
    const s = scoresData[i];
    if (s < 0.05) continue;
    const label = CLASS_LABELS[classData[i]];
    if (label === "van") vanScore = Math.max(vanScore, s);
    else if (label === "non_van") nonVanScore = Math.max(nonVanScore, s);
  }
  const activeThresholds =
    vanScore > nonVanScore ? CLASS_THRESHOLDS_VAN : CLASS_THRESHOLDS;

  // Record what the model classified the vehicle as, for the UI flag.
  lastVanInfo = {
    van: vanScore,
    nonVan: nonVanScore,
    result:
      vanScore === 0 && nonVanScore === 0
        ? "none"
        : vanScore > nonVanScore
        ? "van"
        : "non_van",
  };

  const detections = [];

  // JS loop is now lightning fast because it only reads pre-calculated values
  for (let i = 0; i < count; i++) {
    const score = scoresData[i];
    if (score < 0.05) continue;

    const classId = classData[i];
    const threshold = activeThresholds[classId] || 0.7;

    if (score >= threshold && CLASS_LABELS[classId]) {
      const b = i * 4;

      // Because we used vectorized math above, we just read the values directly now
      const ymin = boxesData[b];
      const xmin = boxesData[b + 1];
      const ymax = boxesData[b + 2];
      const xmax = boxesData[b + 3];

      detections.push({
        bbox: [
          xmin,
          ymin,
          xmax - xmin, // width
          ymax - ymin  // height
        ],
        class: classId,
        label: CLASS_LABELS[classId],
        score,
      });
    }
  }

  // Cleanup both sets of tensors to prevent memory leaks
  cleanupTensors(outputs);
  cleanupTensors(processedTensors);
  return removeVanDetections(detections);
}

/* ================== CLEANUP ================== */

function cleanupTensors(outputs) {
  outputs.forEach((t) => {
    if (t && t.dispose) t.dispose();
  });
}